import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { LocateFixed, Loader2 } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono } from "../ui";
import { getPosition } from "../lib/geo";

const NSW = [-32.3, 147.5];
const num = (v) => (v == null || v === "" || isNaN(v) ? null : Number(v));

function Recenter({ point }) {
  const map = useMap();
  useEffect(() => {
    if (point) map.setView(point, 15);
  }, [point, map]);
  return null;
}
function Fit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
  }, [points, map]);
  return null;
}

export default function FieldMap() {
  const { store, version } = useField();
  const [claims, setClaims] = useState([]);
  const [assets, setAssets] = useState([]);
  const [me, setMe] = useState(null);
  const [locating, setLocating] = useState(false);

  useEffect(() => {
    (async () => {
      setClaims((await store.getClaims()).filter((c) => num(c.latitude) != null));
      setAssets((await store.all("assets")).filter((a) => num(a.latitude) != null));
    })();
  }, [store, version]);

  const points = useMemo(
    () => [...claims, ...assets].map((x) => [num(x.latitude), num(x.longitude)]).filter((p) => p[0] != null),
    [claims, assets]
  );

  const locate = async () => {
    setLocating(true);
    try {
      const pos = await getPosition();
      setMe([pos.latitude, pos.longitude]);
    } catch (e) {
      alert("Could not get your location.");
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="field-map">
      <div className="flex items-center justify-between">
        <div>
          <Mono className="text-gold-400 text-[0.6rem]">Operational picture</Mono>
          <h1 className="font-heading text-2xl mt-1">Field map</h1>
        </div>
        <button onClick={locate} disabled={locating} data-testid="field-locate-me" className="inline-flex items-center gap-1.5 border border-gold-500/25 text-gold-300 text-xs px-3 py-2 rounded-sm">
          {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} My location
        </button>
      </div>

      <div className="border border-gold-500/25 rounded-sm overflow-hidden" style={{ height: "70vh" }} data-testid="field-map-container">
        <MapContainer center={me || points[0] || NSW} zoom={me ? 15 : 6} style={{ height: "100%", width: "100%", background: "#0b1220" }} scrollWheelZoom>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {me ? <Recenter point={me} /> : <Fit points={points} />}
          {assets.map((a) => (
            <CircleMarker key={`a-${a.id}`} center={[num(a.latitude), num(a.longitude)]} radius={7} pathOptions={{ color: "#38bdf8", fillColor: "#38bdf8", fillOpacity: 0.8, weight: 2 }}>
              <Popup><b>{a.name}</b><br />{a.asset_type}</Popup>
            </CircleMarker>
          ))}
          {claims.map((c) => (
            <CircleMarker key={`c-${c.id}`} center={[num(c.latitude), num(c.longitude)]} radius={11} pathOptions={{ color: "#d4a72c", fillColor: "#e9c46a", fillOpacity: 0.9, weight: 3 }}>
              <Popup><b>{c.name}</b><br />{c.council || ""}</Popup>
            </CircleMarker>
          ))}
          {me && (
            <CircleMarker center={me} radius={8} pathOptions={{ color: "#34d399", fillColor: "#34d399", fillOpacity: 0.9, weight: 3 }}>
              <Popup>You are here</Popup>
            </CircleMarker>
          )}
        </MapContainer>
      </div>
      <p className="text-white/40 text-xs text-center">Gold = events · Blue = assets · Green = you</p>
    </div>
  );
}
